import { useEffect, useState } from 'react'
import {
  DayAggregate,
  HardestWord,
  achievementFor,
  TIER_COLORS,
  fetchDayAggregate,
  fetchHardestWord,
  getLastResult,
  globalBaselineAggregate,
  hasEnoughToday,
  isLeaderboardEnabled,
  percentileBeaten,
} from '../../lib/leaderboard'
import { solutionIndex } from '../../lib/words'
import {
  LEADERBOARD_SOLVED_MESSAGE,
  LEADERBOARD_BEATEN_PREFIX,
  LEADERBOARD_BEATEN_SUFFIX_TODAY,
  LEADERBOARD_BEATEN_SUFFIX_TYPICAL,
  HARDEST_WORD_MESSAGE,
} from '../../constants/strings'

// Today's own play, ranked against the day's other players. Leads the
// stats modal, styled as a section of its own.
export const TodayResult = ({ isGameWon }: { isGameWon: boolean }) => {
  const [aggregate, setAggregate] = useState<DayAggregate | null>(null)

  useEffect(() => {
    if (!isLeaderboardEnabled()) return
    fetchDayAggregate(solutionIndex).then(setAggregate)
  }, [])

  const lastResult = getLastResult(solutionIndex)

  // Rank against today once it has enough results, otherwise against the
  // global baseline so a percentile always shows (also works offline).
  const useToday = hasEnoughToday(aggregate)
  const source = useToday ? aggregate! : globalBaselineAggregate()
  const percent =
    lastResult && isGameWon
      ? percentileBeaten(source, lastResult.attempts, true, lastResult.duration)
      : null

  if (percent === null || !lastResult) {
    return null
  }

  return (
    <div className="mb-3">
      <div className="mt-1 text-sm text-gray-500 dark:text-gray-300">
        <p>{LEADERBOARD_SOLVED_MESSAGE(lastResult.attempts)}</p>
        <div className="flex items-center justify-center h-12 my-1 overflow-visible">
          <span className={`medal medal-${achievementFor(percent).tier}`}>
            <span className="medal-inner">{achievementFor(percent).emoji}</span>
          </span>
        </div>
        <div className="flex items-center justify-center flex-wrap gap-x-1.5">
          <span>{LEADERBOARD_BEATEN_PREFIX}</span>
          <span
            className={`pct pct-${
              achievementFor(percent).tier
            } text-2xl font-extrabold ${
              TIER_COLORS[achievementFor(percent).tier]
            }`}
          >
            {percent}&nbsp;%
          </span>
          <span>
            {useToday
              ? LEADERBOARD_BEATEN_SUFFIX_TODAY
              : LEADERBOARD_BEATEN_SUFFIX_TYPICAL}
          </span>
        </div>
      </div>
    </div>
  )
}

// Small footnote under the guess distribution.
export const HardestWordNote = () => {
  const [hardest, setHardest] = useState<HardestWord | null>(null)

  useEffect(() => {
    if (!isLeaderboardEnabled()) return
    fetchHardestWord(solutionIndex).then(setHardest)
  }, [])

  if (!hardest) {
    return null
  }

  return (
    <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
      {HARDEST_WORD_MESSAGE(hardest.solution)}
    </p>
  )
}
